import { post, del, get, patch, put } from '../../ReaderlyHttpClient';
import { ImageData } from '../ItemService/Data';
import { DeliveryInformationPayload } from '../UserService/Data';
import { Merchandise, MerchandiseCategory } from '../ShopService';

export enum OrderStatus {
  Created = 'Created',
  Paid = 'Paid',
  Complectation = 'Complectation',
  Shipped = 'Shipped',
  Returning = 'Returning',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  NeedsClarification = 'NeedsClarification',
  Packed = 'Packed',
}

export interface AdminOrderItem {
  itemId: number;
  count: number;
  name?: string;
  price: number;
  priceType?: string;
  primaryImageUrl?: string;
}

export interface AdminCatalogItem {
  id: number;
  name: string;
  stockCount: number;
  price: number;
  priceType?: string | number;
  primaryImageUrl?: string;
}

export enum BookPriceType {
  Sale = 'Sale',
  Rental = 'Rental',
}

export interface AdminNamedRef {
  id: number;
  name: string;
}

export interface AdminBook {
  id: number;
  name: string;
  description?: string;
  isbn?: string;
  pages: number;
  rating?: number | null;
  internalRating?: number | null;
  publisherId: number;
  publisherName?: string;
  isLimitedEdition: boolean;
  stockCount: number;
  price: number;
  priceType: BookPriceType;
  isDeleted: boolean;
  seriesId?: number | null;
  seriesName?: string;
  seriesNumber?: number | null;
  genres: AdminNamedRef[];
  tags: AdminNamedRef[];
  authors: AdminNamedRef[];
  primaryImage?: ImageData;
  images: ImageData[];
}

export interface AdminBookRequest {
  name?: string;
  description?: string;
  isbn?: string;
  pages?: number;
  rating?: number | null;
  internalRating?: number | null;
  publisherId?: number;
  isLimitedEdition?: boolean;
  stockCount?: number;
  price?: number;
  priceType?: BookPriceType;
  seriesId?: number | null;
  newSeriesName?: string;
  seriesNumber?: number | null;
  genreIds?: number[];
  tagIds?: number[];
  authorIds?: number[];
  newAuthorNames?: string[];
}

export interface AdminDeliveryInformation {
  id?: number;
  name: string;
  lastName: string;
  phone: string;
  provider: string | number;
  destination: string | number;
  city: string;
  number: string;
  displayName?: string;
  countryCode?: string;
  isPrimary: boolean;
}

export interface AdminOrder {
  id: number;
  userId: number;
  userEmail: string;
  userName: string;
  userLastName: string;
  userPhoneNumber: string;
  orderDate: string;
  totalPrice: number;
  orderStatus: OrderStatus;
  deliveryType: string;
  userComment?: string;
  comment?: string;
  trackingNumber?: string;
  userDeliveryInformation: AdminDeliveryInformation;
  items: AdminOrderItem[];
}

export interface PagingResponse<T> {
  totalCount: number;
  items: T[];
}

export interface UpdateOrderStatusRequest {
  status: OrderStatus;
  comment?: string;
  trackingNumber?: string;
}

export interface UpdateOrderItemsRequest {
  items: Array<{ itemId: number; count: number }>;
  comment?: string;
}

export enum UserRole {
  Client = 'Client',
  Admin = 'Admin',
  Seller = 'Seller',
}

export enum UserStatus {
  Active = 'Active',
  Block = 'Block',
  Deleted = 'Deleted',
}

export interface AdminUser {
  id: number;
  name: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  nickname?: string;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  hasLogin: boolean;
}

export interface AdminUserRequest {
  name?: string;
  lastName?: string;
  phoneNumber?: string;
  nickname?: string;
  role?: UserRole;
  status?: UserStatus;
}

export const uploadBookImage = (bookId: number, file: File, isPrimary: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('isPrimary', String(isPrimary));

  return post<ImageData, FormData>(`/api/admin/books/${bookId}/images`, formData, undefined, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteBookImage = (imageId: number) => {
  return del<void>(`/api/admin/books/images/${imageId}`);
};

export interface ImportBooksResult {
  importedCount: number;
}

export const importBooksFromCsv = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return post<ImportBooksResult, FormData>('/api/admin/books/import', formData, undefined, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getAdminBooks = (search = '', index = 0, count = 50) => {
  return get<PagingResponse<AdminBook>>('/api/admin/books', {
    search,
    index,
    count,
  });
};

export const getAdminBook = (bookId: number) => {
  return get<AdminBook>(`/api/admin/books/${bookId}`);
};

export const createAdminBook = (request: AdminBookRequest) => {
  return post<AdminBook, AdminBookRequest>('/api/admin/books', request);
};

export const updateAdminBook = (bookId: number, request: AdminBookRequest) => {
  return put<{ id: number }, AdminBookRequest>(`/api/admin/books/${bookId}`, request);
};

export const deleteAdminBook = (bookId: number) => {
  return del<void>(`/api/admin/books/${bookId}`);
};

export const deleteAdminBookPermanently = (bookId: number) => {
  return del<void>(`/api/admin/books/${bookId}/permanent`);
};

export const getOrders = (status?: OrderStatus, index = 0, count = 200) => {
  return get<PagingResponse<AdminOrder>>('/api/admin/orders', {
    index,
    count,
    status: status || undefined,
  });
};

export const getCatalogItems = (search: string, index = 0, count = 20) => {
  return get<AdminCatalogItem[]>('/items/latest', {
    search,
    index,
    count,
  }, { suppressGlobalError: true });
};

export const getOrder = (orderId: number) => {
  return get<AdminOrder>(`/api/admin/orders/${orderId}`);
};

export const getOrdersByUser = (userId: number) => {
  return get<AdminOrder[]>(`/api/admin/orders/by-user/${userId}`);
};

export const updateOrderStatus = (orderId: number, request: UpdateOrderStatusRequest) => {
  return patch<AdminOrder, UpdateOrderStatusRequest>(`/api/admin/orders/${orderId}/status`, request);
};

export const updateOrderItems = (orderId: number, request: UpdateOrderItemsRequest) => {
  return put<AdminOrder, UpdateOrderItemsRequest>(`/api/admin/orders/${orderId}/items`, request);
};

export const getAdminUsers = (search = '', index = 0, count = 50) => {
  return get<PagingResponse<AdminUser>>('/api/admin/users', {
    search,
    index,
    count,
  });
};

export const getAdminUser = (userId: number) => {
  return get<AdminUser>(`/api/admin/users/${userId}`);
};

export const updateAdminUser = (userId: number, request: AdminUserRequest) => {
  return put<AdminUser, AdminUserRequest>(`/api/admin/users/${userId}`, request);
};

export interface CreateAdminOrderRequest {
  userId: number;
  items: Array<{ itemId: number; count: number }>;
  userComment?: string;
  comment?: string;
  deliveryAddress: DeliveryInformationPayload;
}

export const createAdminOrder = (request: CreateAdminOrderRequest) => {
  return post<AdminOrder, CreateAdminOrderRequest>('/api/admin/orders', request);
};

export interface AdminUserCreateRequest {
  name: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
}

export const createAdminUser = (request: AdminUserCreateRequest) => {
  return post<AdminUser, AdminUserCreateRequest>('/api/admin/users', request);
};

export interface AdminCollectionBookRef {
  id: number;
  name: string;
  authors: { id: number; name: string }[];
  primaryImage?: ImageData;
}

export interface AdminCollectionItem {
  book: AdminCollectionBookRef;
  comment?: string;
  sortOrder: number;
}

export interface AdminCollection {
  id: number;
  title: string;
  slug: string;
  description?: string;
  coverImageUrl?: string;
  isActive: boolean;
  sortOrder: number;
  items: AdminCollectionItem[];
}

export interface AdminCollectionItemRequest {
  bookId: number;
  sortOrder: number;
  comment?: string;
}

export interface AdminCollectionRequest {
  title: string;
  slug: string;
  description?: string;
  coverImageUrl?: string;
  isActive: boolean;
  sortOrder: number;
  items: AdminCollectionItemRequest[];
}

export const getAdminCollections = () => {
  return get<AdminCollection[]>('/api/admin/book-collections');
};

export const createAdminCollection = (request: AdminCollectionRequest) => {
  return post<{ id: number }, AdminCollectionRequest>('/api/admin/book-collections', request);
};

export const updateAdminCollection = (id: number, request: AdminCollectionRequest) => {
  return put<void, AdminCollectionRequest>(`/api/admin/book-collections/${id}`, request);
};

export const deleteAdminCollection = (id: number) => {
  return del<void>(`/api/admin/book-collections/${id}`);
};

export enum WaitlistSource {
  Site = 'Site',
  Instagram = 'Instagram',
  Other = 'Other',
}

export interface AdminWaitlistEntry {
  id: number;
  position: number;
  source: WaitlistSource;
  userId?: number | null;
  name: string;
  email?: string | null;
  phone?: string | null;
  contact?: string | null;
  note?: string | null;
  createdAt: string;
}

export interface AdminWaitlistItem {
  itemId: number;
  itemName: string;
  stockCount: number;
  entries: AdminWaitlistEntry[];
}

export interface AdminWaitlistEntryRequest {
  source: WaitlistSource;
  contactName?: string;
  contact?: string;
  note?: string;
}

export const getAdminWaitlists = () => {
  return get<AdminWaitlistItem[]>('/api/admin/waitlist');
};

export const getAdminWaitlist = (itemId: number) => {
  return get<AdminWaitlistItem>(`/api/admin/waitlist/${itemId}`);
};

export const addAdminWaitlistEntry = (itemId: number, request: AdminWaitlistEntryRequest) => {
  return post<AdminWaitlistItem, AdminWaitlistEntryRequest>(`/api/admin/waitlist/${itemId}`, request);
};

export const updateAdminWaitlistEntry = (entryId: number, request: AdminWaitlistEntryRequest) => {
  return put<AdminWaitlistItem, AdminWaitlistEntryRequest>(`/api/admin/waitlist/entries/${entryId}`, request);
};

export const removeAdminWaitlistEntry = (entryId: number) => {
  return del<AdminWaitlistItem>(`/api/admin/waitlist/entries/${entryId}`);
};

export const reorderAdminWaitlist = (itemId: number, entryIds: number[]) => {
  return put<AdminWaitlistItem, { entryIds: number[] }>(`/api/admin/waitlist/${itemId}/order`, { entryIds });
};

export interface AdminMerchandiseRequest {
  name?: string;
  description?: string;
  details?: string;
  category?: MerchandiseCategory;
  stockCount?: number;
  price?: number;
}

export const getAdminMerchandise = (search = '', index = 0, count = 100) => {
  return get<PagingResponse<Merchandise>>('/api/admin/merchandise', { search: search || undefined, index, count });
};

export const getAdminMerchandiseItem = (id: number) => {
  return get<Merchandise>(`/api/admin/merchandise/${id}`);
};

export const createAdminMerchandise = (request: AdminMerchandiseRequest) => {
  return post<Merchandise, AdminMerchandiseRequest>('/api/admin/merchandise', request);
};

export const updateAdminMerchandise = (id: number, request: AdminMerchandiseRequest) => {
  return put<Merchandise, AdminMerchandiseRequest>(`/api/admin/merchandise/${id}`, request);
};

export const deleteAdminMerchandise = (id: number) => {
  return del<void>(`/api/admin/merchandise/${id}`);
};

export const restoreAdminMerchandise = (id: number) => {
  return post<void, {}>(`/api/admin/merchandise/${id}/restore`, {});
};

export const uploadMerchandiseImage = (id: number, file: File, isPrimary: boolean) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('isPrimary', String(isPrimary));

  return post<ImageData, FormData>(`/api/admin/merchandise/${id}/images`, formData, undefined, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const setMerchandisePrimaryImage = (imageId: number) => {
  return post<void, {}>(`/api/admin/merchandise/images/${imageId}/primary`, {});
};

export const deleteMerchandiseImage = (imageId: number) => {
  return del<void>(`/api/admin/merchandise/images/${imageId}`);
};

export interface AdminShopInquiry {
  id: number;
  itemId: number;
  itemName: string;
  userId?: number | null;
  name: string;
  phone: string;
  message?: string | null;
  createdAt: string;
  handledAt?: string | null;
}

export const getAdminShopInquiries = (isHandled?: boolean, index = 0, count = 50) => {
  return get<PagingResponse<AdminShopInquiry>>('/api/admin/merchandise/inquiries', { isHandled, index, count });
};

export const setAdminShopInquiryHandled = (id: number, isHandled: boolean) => {
  return put<void, { isHandled: boolean }>(`/api/admin/merchandise/inquiries/${id}/handled`, { isHandled });
};